import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
} from 'react'

import { SuggestionProps } from '@tiptap/suggestion'
import clsx from 'clsx'
import _debounce from 'lodash/debounce'

import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'

import { Mention } from '../../@types'
import { MenuList } from '../../components/MenuList'

export type MentionListProps = SuggestionProps & {
  search: (variables: { query: string }) => Promise<Mention[]>
  items: any[]
  command: any
}

export const MentionList = forwardRef<unknown, MentionListProps>(
  ({ search, command, query }, ref) => {
    const refs = useRef([])
    const [items, setItems] = useState<Mention[]>(undefined)
    const [loading, setLoading] = useState<boolean>(false)
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = index => {
      const item = items?.[index]

      if (item) {
        command({ id: item.id, label: item.title })
      }
    }

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length)
    }

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length)
    }

    const enterHandler = () => {
      selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [items])

    useEffect(() => {
      const el = refs.current[selectedIndex]
      if (el) {
        el.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'start',
        })
      }
    }, [selectedIndex])

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }

        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }

        if (event.key === 'Enter') {
          enterHandler()
          return true
        }

        return false
      },
    }))

    const debounce = useCallback(
      _debounce((debouncedQuery: string) => {
        if (typeof search === 'function') {
          search({
            query: debouncedQuery,
          })
            .then(items => {
              setItems(items)
            })
            .finally(() => {
              setLoading(false)
            })
        }
      }, 300),
      [],
    )

    useEffect(() => {
      setLoading(true)
      debounce(query)
    }, [query, debounce])

    if (!query) return null

    if (loading) {
      return (
        <MenuList>
          <div className="w-28 flex justify-center items-center px-4 py-2">
            <SpinnerIcon className="animate-spin" />
          </div>
        </MenuList>
      )
    }

    if (!items || items.length === 0) {
      return (
        <MenuList>
          <p className="text-center bg-surface-50 text-basicSurface-700 flex items-center px-4 py-2 space-x-2">
            Couldn&apos;t find any match
          </p>
        </MenuList>
      )
    }

    return (
      <MenuList>
        {items.map((item, index) => (
          <span
            ref={el => {
              refs.current[index] = el
            }}
            className={clsx(
              index === selectedIndex
                ? 'bg-surface-100 text-basicSurface-900'
                : 'bg-surface-50 text-basicSurface-700',
              'flex items-center px-4 py-2 space-x-2',
              'cursor-pointer',
            )}
            key={item.id}
            onClick={() => selectItem(index)}
          >
            <Avatar size="xs" src={item.icon} name={item.title} />
            <p className="ml-2 truncate">{item.title}</p>
          </span>
        ))}
      </MenuList>
    )
  },
)
